// Mirrored from mju-ai-workbench/shared/schemas/tools.ts — keep the two identical but for this line and the import extensions.
/**
 * Governing the tools agents use (MCP servers): docs/plans/2026-10-mcp-tool-governance.md.
 * AIOE-first. The workbench mirrors this file when it builds its gate; until then it reports
 * nothing here and ignores the `tools` clause of the policy.
 *
 * Four things. A **tool server** is published in the catalogue (kind `mcp`). A **tool** is what a
 * server offers, reported by the machines that start it and reviewed in the console. A **rule**
 * says Allowed, Ask first or Off. A **call** is one use, decided by `toolDecision` and recorded.
 *
 *   POST /tools/v1/inventory          a machine reports the servers it starts and their tools (device)
 *   POST /tools/v1/counts             allowed calls per server, tool and hour (device); the same hour sent again replaces it
 *   GET  /admin/v1/tools              every server, published or only seen, with its tools (administrators and operators)
 *   PUT  /admin/v1/tools/:fingerprint { names, action?, risk?, reviewed? }: set, classify or review tools (administrators)
 *   GET  /catalogue/v1?include=mcp    the catalogue with its tool servers (left out unless asked for, so an
 *                                     older workbench that knows four kinds still parses it)
 *   GET  /policy/v1                   carries `tools`, with `servers` filled in from the catalogue and the reviews
 *
 * A server is known by a **fingerprint** of how it is started or reached, never by its name: a
 * repository cannot call its own server "tracker" and inherit the organisation's review of it.
 */
import { z } from "zod";
import { canonicalJson } from "./nodeManifest.js";
import { labelMatchSchema, labelsMatch } from "./sensitivityLabels.js";
export const TOOL_RISKS = ["reads", "changes", "destroys"];
export const toolRiskSchema = z.enum(TOOL_RISKS);
/** A 1-5 scale beside the three classes, so an approval can say how serious it is. Additive: older readers ignore it. */
export const toolRiskLevelSchema = z.number().int().min(1).max(5);
export const RISK_LEVEL_WORDS = { 1: "Routine", 2: "Low", 3: "Needs a look", 4: "Needs approval", 5: "Halts until approved" };
/** The level a class implies when nobody set one: reads 1, changes 3, destroys 5. */
export const defaultRiskLevel = (risk) => (risk === "reads" ? 1 : risk === "changes" ? 3 : 5);
/** "Risk level 4 out of 5: needs approval", for an inbox row. */
export const riskLevelSentence = (level) => {
    const w = RISK_LEVEL_WORDS[level] ?? "";
    return `Risk level ${level} out of 5${w ? `: ${w.charAt(0).toLowerCase()}${w.slice(1)}` : ""}`;
};
/** Whose words a tool's answer carries: the workbench's own tools; files in the worktree; anyone else's (web, mail, chats, tickets, knowledge bases). */
export const TOOL_RESULT_ORIGINS = ["own", "repository", "outside"];
export const toolResultOriginSchema = z.enum(TOOL_RESULT_ORIGINS);
/** What the gate does once a session has read outside content: ignore it, record it on the call, or ask a person first. */
export const AFTER_OUTSIDE = ["off", "record", "ask"];
export const afterOutsideSchema = z.enum(AFTER_OUTSIDE);
/** A rule's `match` that holds once the calling session has read outside content (`ToolCall.outside`). */
export const OUTSIDE_MATCH = "outside";
/** The words a person reads for each. */
export const RESULT_ORIGIN_WORDS = { own: "Answers come from the workbench itself", repository: "Answers come from the repository", outside: "Answers come from outside the organisation" };
export const AFTER_OUTSIDE_LABEL = { off: "Ignore it", record: "Record it on the call", ask: "Ask a person first" };
export const TOOL_ACTIONS = ["allow", "ask", "off"];
export const toolActionSchema = z.enum(TOOL_ACTIONS);
/** The words a person reads for each. */
export const TOOL_ACTION_LABEL = { allow: "Allowed", ask: "Ask first", off: "Off" };
export const TOOL_RISK_LABEL = { reads: "Reads", changes: "Changes", destroys: "Destroys" };
const STRICTNESS = { allow: 0, ask: 1, off: 2 };
/** The stricter of two: Off, then Ask first, then Allowed. */
export const stricter = (a, b) => (STRICTNESS[a] >= STRICTNESS[b] ? a : b);
/** How a server is started (`stdio`) or reached (`http`). Credentials are named, never held. */
export const toolServerDefinitionSchema = z
    .object({
    transport: z.enum(["stdio", "http"]),
    command: z.string().min(1).max(500).optional(),
    args: z.array(z.string().max(1000)).max(50).default([]),
    url: z.string().min(1).max(2000).optional(),
    /** The names of the environment variables and headers it needs. The machine supplies the values. */
    env: z.array(z.string().min(1).max(200)).max(50).default([]),
    headers: z.array(z.string().min(1).max(200)).max(50).default([]),
    /**
     * A web service that takes a person's Microsoft Entra token but does not say so in its own
     * discovery (Azure AI Search knowledge bases, Fabric data agents): the machine signs the person in
     * with the organisation's app for it (discovery's `microsoftSignIn`) for `scope` and sends the
     * token as a bearer. Left out: the server's own OAuth discovery, if any, decides.
     */
    auth: z.object({ kind: z.literal("entra"), scope: z.string().min(1).max(300) }).optional(),
})
    .refine((d) => (d.transport === "stdio" ? Boolean(d.command) : Boolean(d.url)), { message: "a server that runs on the machine needs a command; a web service needs an address" });
/**
 * The content of a catalogue entry of kind `mcp`. A withdrawn server stays listed so its tools stay Off.
 * `tools` (Hermes item 1, docs/plans/2026-10-hermes-adoption.md): the tool names the submitter says the server
 * offers; a node checks the claim (`toolserver.verify`), and an entry registering tools it did not declare is
 * not approved until the declaration says so.
 */
export const toolServerEntrySchema = toolServerDefinitionSchema.safeExtend({
    status: z.enum(["published", "withdrawn"]).default("published"),
    tools: z.array(z.string().min(1).max(200)).max(500).optional(),
});
const sha256Hex = async (text) => [...new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text)))].map((b) => b.toString(16).padStart(2, "0")).join("");
/** What identifies a server: its transport and its command and arguments, or its address. Names of credentials are not part of it. */
export function serverFingerprint(d) {
    return sha256Hex(canonicalJson(d.transport === "stdio" ? { transport: "stdio", command: d.command ?? "", args: d.args ?? [] } : { transport: "http", url: (d.url ?? "").replace(/\/+$/, "") }));
}
/** What a review pins: the tool's name, the words the agent reads and the shape of its input. Any change makes it wait for review again. */
export function toolHash(t) {
    return sha256Hex(canonicalJson({ name: t.name, description: t.description ?? "", inputSchema: t.inputSchema ?? null }));
}
/** A server's own claim about a tool. A hint, not a fact: an administrator can correct the class. */
export const toolAnnotationsSchema = z.object({ readOnlyHint: z.boolean().optional(), destructiveHint: z.boolean().optional() });
export const riskOf = (a) => (a?.destructiveHint ? "destroys" : a?.readOnlyHint ? "reads" : "changes");
const hex64 = z.string().regex(/^[0-9a-f]{64}$/);
export const toolReportSchema = z.object({
    name: z.string().min(1).max(200),
    description: z.string().max(4000).optional(),
    /** `toolHash` of the tool as the server offered it. */
    hash: hex64,
    annotations: toolAnnotationsSchema.optional(),
});
export const toolServerReportSchema = z.object({
    /** What the configuration calls it. */
    name: z.string().min(1).max(200),
    fingerprint: hex64,
    definition: toolServerDefinitionSchema,
    tools: z.array(toolReportSchema).max(500).default([]),
    /** Why its tools could not be listed, when it did not start. */
    error: z.string().max(500).optional(),
    /** Runtimes (by id) that also read this server from the repository's own configuration, so the gate is not their only path to it. */
    alsoReadBy: z.array(z.string().min(1).max(100)).max(20).optional(),
});
/** `POST /tools/v1/inventory`: everything this machine starts for the project. It replaces the machine's last report of each server. */
export const toolInventorySchema = z.object({
    projectId: z.string().min(1).max(100).optional(),
    servers: z.array(toolServerReportSchema).max(200),
});
/** `POST /tools/v1/counts`: calls that were let through, by the hour (UTC, ISO, on the hour). Refusals and held calls are audit rows. */
export const toolCountsSchema = z.object({
    counts: z
        .array(z.object({ fingerprint: hex64, tool: z.string().min(1).max(200), hour: z.string().min(13).max(30), calls: z.number().int().nonnegative().max(10_000_000) }))
        .max(2000),
});
/* ── the policy clause ─────────────────────────────────────────────────────────────── */
export const toolRuleSchema = z.object({
    /** The catalogue id or the name of a server; `*` for every server. */
    server: z.string().min(1).max(200),
    /** Tool names, `*` standing for any run of characters; every tool when left out. */
    tools: z.array(z.string().min(1).max(200)).max(200).optional(),
    /** Only tools of this class. */
    risk: toolRiskSchema.optional(),
    action: toolActionSchema,
    /** Only for agents in these team roles; every agent when left out. */
    roles: z.array(z.string().min(1).max(100)).max(50).optional(),
    /**
     * Only while the calling session has seen content carrying this Purview label (`label:<id or name>`,
     * shared/schemas/sensitivityLabels.ts): "once a session has read Highly Confidential, fetch is off".
     * `outside`: only once the session has read outside content (`ToolCall.outside`).
     */
    match: z.union([z.literal(OUTSIDE_MATCH), labelMatchSchema]).optional(),
});
/** One reviewed tool as the gate needs it. */
export const governedToolSchema = z.object({
    risk: toolRiskSchema,
    /** The 1-5 level; the class default (`defaultRiskLevel`) when left out. */
    riskLevel: toolRiskLevelSchema.optional(),
    /** The organisation's setting for it; the class default when left out. */
    action: toolActionSchema.optional(),
    /** The reviewed `toolHash`; absent while nobody has reviewed it. */
    hash: hex64.optional(),
    /** The reviewed words: what the agent is shown while a changed description waits for review. */
    description: z.string().max(4000).optional(),
    /** Whose words its answer carries; an answer from `outside` marks the session. */
    resultOrigin: toolResultOriginSchema.optional(),
    /** It reads data the person can see that the repository cannot (mail, files, chats, calendars). */
    readsPrivate: z.boolean().optional(),
});
export const governedServerSchema = z.object({
    /** The catalogue entry. */
    id: z.string().min(1).max(100),
    name: z.string().min(1).max(200),
    fingerprint: hex64,
    status: z.enum(["published", "withdrawn"]),
    tools: z.record(z.string(), governedToolSchema).default({}),
});
export const toolsPolicySchema = z.object({
    /** `audit`: record what would happen and let the call through. `enforce`: do it. */
    enforcement: z.enum(["audit", "enforce"]).default("audit"),
    /** A server from a repository that is not in the catalogue. */
    unlistedServers: z.enum(["allow", "ask", "block"]).default("allow"),
    /** What a tool of each class gets when nothing else says. */
    defaults: z.object({ reads: toolActionSchema.default("allow"), changes: toolActionSchema.default("ask"), destroys: toolActionSchema.default("off") }).prefault({}),
    rules: z.array(toolRuleSchema).max(500).default([]),
    /** Whether a recorded call keeps its arguments, masked, or only their digest. */
    recordArguments: z.enum(["none", "redacted"]).default("redacted"),
    /** Once a session has read outside content: `off` ignores it, `record` marks the call, `ask` puts changes, deletions and private reads to a person. */
    afterOutside: afterOutsideSchema.default("record"),
    /** Filled in by AIOE from the catalogue and the reviews (never stored on a policy record). */
    servers: z.array(governedServerSchema).max(500).optional(),
    /**
     * Hermes item 2 (docs/plans/2026-10-hermes-adoption.md): the organisation's own command patterns, refused
     * before any mode, grant or bypass, like the shipped floor (TOOL_FLOOR). fnmatch-style globs over the
     * normalised command line: `*` any run of characters, `?` one, `[abc]` a set.
     */
    denyCommands: z.array(z.string().min(1).max(300)).max(200).default([]),
});
// ── The floor (Hermes item 2) ──────────────────────────────────────────
export const TOOL_FLOOR_VERSION = 2;
/**
 * Commands no mode, grant, approval or bypass may let through: the workbench or node runs `commandFloor`
 * before anything else it consults, a runtime's own bypass mode included. Shipped with the platform and
 * never removable by an organisation (it adds `denyCommands`). A refusal is not an approval request: the
 * command did not run, the agent is told the rule, and the run and the audit trail record `tool.floor`.
 * Patterns are regular expressions over the normalised command line (`normaliseCommand`).
 */
export const TOOL_FLOOR = [
    { id: "wipe-root", words: "recursive deletion of the root, the home directory, the parent directory or everything", pattern: String.raw `(^|[;&|(]\s*|["']\s*)rm\s+(-[a-z]*r[a-z]*f[a-z]*|-[a-z]*f[a-z]*r[a-z]*|-r\s+-f|-f\s+-r)\s+(--no-preserve-root\s+)?["']?(/|~|\$home|\.\.|\*)["']?(\s|\*|$)` },
    { id: "fork-bomb", words: "a fork bomb", pattern: String.raw `:\s*\(\s*\)\s*\{\s*:\s*\|\s*:\s*&\s*\}\s*;\s*:` },
    { id: "block-device-write", words: "writing straight to a disk device", pattern: String.raw `(dd\s+.*\bof=/dev/(sd|nvme|hd|vd|xvd|mmcblk|disk)|\bmkfs(\.[a-z0-9]+)?\s|\bshred\s+.*\s/dev/|>\s*/dev/(sd|nvme|hd|vd|xvd|mmcblk|disk))` },
    { id: "sudo-password", words: "feeding a password to sudo", pattern: String.raw `(sudo\s+(-\S+\s+)*-s\b|\|\s*sudo\b)` },
    { id: "chmod-world-root", words: "making the whole filesystem world-writable", pattern: String.raw `chmod\s+(-[a-z]*r[a-z]*\s+)?(777|a\+rwx|o\+w)\s+["']?/["']?(\s|$)` },
    { id: "kill-own-service", words: "stopping or killing the node service or the workbench this command runs under", pattern: String.raw `((systemctl\s+(--\S+\s+)*|service\s+|sc(\.exe)?\s+|launchctl\s+)(stop|disable|kill|restart|unload|bootout|delete|remove|kickstart\s+-k)\s+.*(mojoup-node|aioe-mesh|aioe-node|mojoup-workbench|aioe-gateway)|schtasks(\.exe)?\s+/end\s+.*(mojoup-node|aioe-mesh|aioe-node|mojoup-workbench|aioe-gateway)|(pkill|killall|taskkill(\.exe)?|stop-process)\s+.*(mojoup-node|aioe-mesh|aioe-node|mojoup-workbench|aioe-gateway))` },
];
/**
 * The command line as the floor sees it: whitespace collapsed, a leading `sudo`, `env VAR=…`, `sh -c`, `bash -c`
 * or `cmd /c` wrapper and its quotes stripped (so the floor reads what would run), lower-cased.
 */
export function normaliseCommand(command) {
    let c = command.replace(/\s+/g, " ").trim();
    for (let i = 0; i < 4; i++) {
        const m = /^(?:sudo\s+(?:-\S+\s+)*|env\s+(?:[A-Za-z_][A-Za-z0-9_]*=\S*\s+)+|(?:ba|z|da)?sh\s+-l?c\s+|cmd(?:\.exe)?\s+\/c\s+|powershell(?:\.exe)?\s+(?:-\S+\s+)*-c(?:ommand)?\s+)/i.exec(c);
        if (!m)
            break;
        c = c.slice(m[0].length).trim();
        if ((c.startsWith('"') && c.endsWith('"')) || (c.startsWith("'") && c.endsWith("'")))
            c = c.slice(1, -1).trim();
    }
    return c.toLowerCase();
}
/** `*`, `?` and `[…]` as fnmatch has them, against the whole normalised command line. */
function globToRegExp(glob) {
    let out = "^";
    for (let i = 0; i < glob.length; i++) {
        const ch = glob[i];
        if (ch === "*")
            out += ".*";
        else if (ch === "?")
            out += ".";
        else if (ch === "[") {
            const end = glob.indexOf("]", i + 1);
            if (end > i) {
                out += `[${glob.slice(i + 1, end).replace(/\\/g, "\\\\")}]`;
                i = end;
            }
            else
                out += "\\[";
        }
        else
            out += ch.replace(/[.+^${}()|\\/]/g, "\\$&");
    }
    return new RegExp(out + "$", "is");
}
/**
 * The floor's decision for one command line: refused by a shipped rule, by one of the organisation's own
 * globs, or not at all. Pure; the same on every side. An invalid glob is skipped, never a refusal.
 */
export function commandFloor(command, denyCommands = []) {
    const c = normaliseCommand(command);
    if (!c)
        return { refused: false };
    for (const r of TOOL_FLOOR)
        if (new RegExp(r.pattern, "i").test(c))
            return { refused: true, by: "floor", rule: r.id, words: r.words };
    for (const g of denyCommands) {
        try {
            if (globToRegExp(g.toLowerCase()).test(c))
                return { refused: true, by: "denyCommands", rule: g, words: `your organisation refuses commands matching ${g}` };
        }
        catch {
            /* an unusable glob refuses nothing */
        }
    }
    return { refused: false };
}
const nameMatches = (pattern, name) => new RegExp(`^${pattern.split("*").map((x) => x.replace(/[.+?^${}()|[\]\\]/g, "\\$&")).join(".*")}$`).test(name);
/**
 * The one definition of what the clause means for a call: what the workbench's gate enforces and
 * what the console previews.
 *
 *   1. a withdrawn server: Off;
 *   2. a server not in the catalogue: `unlistedServers` (block: Off; ask: Ask first; allow: Allowed),
 *      made stricter by any rule that names it;
 *   3. a listed server: the tool's own setting and every matching rule, the strictest winning;
 *      the class default when none says anything;
 *   4. a tool waiting for review that is not Reads is never Allowed: Ask first at most;
 *   5. a session that has read outside content (`call.outside`), with `afterOutside: ask`: a tool
 *      that changes, destroys or reads private data is never Allowed (Ask first, `by: outside`),
 *      and its risk level is one higher, at most 5; Off stays Off. With `record` the verdict only
 *      carries `outside`; with `off` the mark is ignored, and so is a rule's `outside` match;
 *   6. `enforcement: audit` lets the call through and says what would have happened.
 */
export function toolDecision(policy, call) {
    const server = policy.servers?.find((s) => s.fingerprint === call.fingerprint);
    const known = server?.tools[call.tool];
    const risk = known?.risk ?? call.risk ?? "changes";
    const waiting = Boolean(server) && (!known?.hash || (call.hash !== undefined && call.hash !== known.hash));
    const marked = Boolean(call.outside) && policy.afterOutside !== "off";
    const raise = marked && policy.afterOutside === "ask" && (risk !== "reads" || known?.readsPrivate === true);
    const ownLevel = known?.riskLevel ?? call.riskLevel ?? defaultRiskLevel(risk);
    const riskLevel = raise ? Math.min(5, ownLevel + 1) : ownLevel;
    const finish = (decided, decidedBy, decidedReason) => {
        const held = raise && decided === "allow";
        const action = held ? "ask" : decided;
        const by = held ? "outside" : decidedBy;
        const reason = held ? `this session has read outside content: a person is asked before ${call.tool} ${risk === "changes" ? "changes things" : risk === "destroys" ? "destroys things" : "reads private data"}` : decidedReason;
        const mark = marked ? { outside: true } : {};
        if (action !== "allow" && policy.enforcement === "audit")
            return { action: "allow", would: action, by, reason, risk, riskLevel, waiting, ...mark };
        return { action, by, reason, risk, riskLevel, waiting, ...mark };
    };
    if (server?.status === "withdrawn")
        return finish("off", "withdrawn", `${server.name} has been withdrawn`);
    const names = server ? [server.id, server.name] : call.serverName ? [call.serverName] : [];
    let fromRules;
    policy.rules.forEach((rule, i) => {
        if (rule.server !== "*" && !names.includes(rule.server))
            return;
        if (rule.tools && !rule.tools.some((p) => nameMatches(p, call.tool)))
            return;
        if (rule.risk && rule.risk !== risk)
            return;
        if (rule.roles && !(call.role && rule.roles.includes(call.role)))
            return;
        if (rule.match && !(rule.match === OUTSIDE_MATCH ? marked : labelsMatch(rule.match, call.labels)))
            return;
        if (!fromRules || STRICTNESS[rule.action] > STRICTNESS[fromRules.action])
            fromRules = { action: rule.action, by: `rule:${i + 1}` };
    });
    const label = (a) => TOOL_ACTION_LABEL[a].toLowerCase();
    if (!server) {
        const base = policy.unlistedServers === "block" ? "off" : policy.unlistedServers === "ask" ? "ask" : "allow";
        if (fromRules && STRICTNESS[fromRules.action] > STRICTNESS[base])
            return finish(fromRules.action, fromRules.by, `a rule sets ${call.tool} to ${label(fromRules.action)}`);
        return finish(base, "unlisted", base === "allow" ? "the server is not in the catalogue, and unlisted servers are allowed" : base === "ask" ? "the server is not in the catalogue: a person is asked first" : "the server is not in the catalogue, and unlisted servers are blocked");
    }
    let decided;
    if (known?.action && (!fromRules || STRICTNESS[known.action] >= STRICTNESS[fromRules.action]))
        decided = { action: known.action, by: "tool", reason: `${call.tool} is set to ${label(known.action)}` };
    else if (fromRules)
        decided = { ...fromRules, reason: `a rule sets ${call.tool} to ${label(fromRules.action)}` };
    else
        decided = { action: policy.defaults[risk], by: "default", reason: `a tool that ${risk === "reads" ? "only reads" : risk === "changes" ? "changes things" : "destroys things"} is ${label(policy.defaults[risk])} by default` };
    if (waiting && risk !== "reads" && decided.action === "allow")
        return finish("ask", "review", `${call.tool} is ${known?.hash ? "changed since it was reviewed" : "new and not reviewed"}: a person is asked first`);
    return finish(decided.action, decided.by, decided.reason);
}
/** `PUT /admin/v1/tools/:fingerprint`. `action: null` returns the tools to their class default. */
export const toolReviewSchema = z.object({
    names: z.array(z.string().min(1).max(200)).min(1).max(500),
    action: toolActionSchema.nullable().optional(),
    risk: toolRiskSchema.optional(),
    riskLevel: toolRiskLevelSchema.optional(),
    /** Whose words their answers carry. */
    resultOrigin: toolResultOriginSchema.optional(),
    /** Whether they read data the person can see that the repository cannot. */
    readsPrivate: z.boolean().optional(),
    /** Pin the tools as they are offered now. */
    reviewed: z.literal(true).optional(),
});
//# sourceMappingURL=tools.js.map